/**
 * ¿Llega este courier a este destino? UNA sola regla para la lista de couriers y para el registro.
 *
 * 🚨 DE DÓNDE SALE (founder, 3-oct). La cobertura de Eva estaba escrita tres veces, y dos decían otra cosa que la
 *    tabla: el bloque de Eva en Entrega y el mapa viejo daban por cubierto «Lima provincia + Callao». La tabla —que
 *    `eva-coverage-sync` arma con el Coberturero de Eva— dice otra cosa en los dos sentidos: Eva NO llega a Pucusana,
 *    Punta Hermosa, Punta Negra, San Bartolo, Santa María del Mar ni Mi Perú (la pantalla decía «Cubre Lima» y Eva
 *    rebotaba el registro), y SÍ llega a Ricardo Palma, San Antonio y Santa Eulalia, en Huarochirí (la pantalla
 *    decía «Fuera de zona» y no dejaba registrar).
 *
 * ⭐ AHORA LA TABLA ES LA ÚNICA VERDAD, con el mismo criterio que usa el resolvedor para ofrecer el courier
 *    (`filaAlcanzaElDestino`): lo que la lista ofrece y lo que Entrega deja registrar no se pueden contradecir.
 */
import { supabase } from '@/lib/supabase';
import { claveDeLugar, mismoLugar } from '@/lib/ubigeo';

export interface FilaDeCobertura {
  department?: string | null;
  province?: string | null;
  district?: string | null;
}

/** El destino ya pasado a CLAVE (`claveDeLugar`): así se compara una vez, no en cada fila. */
export interface ClaveDelDestino {
  dept: string | null;
  prov: string | null;
  dist: string | null;
}

/** ¿Esta fila de cobertura alcanza el destino? Sin valor en la fila es «cualquiera»; con valor, por clave. */
export const filaAlcanzaElDestino = (fila: FilaDeCobertura, clave: ClaveDelDestino): boolean =>
  mismoLugar(fila.department, clave.dept) && mismoLugar(fila.province, clave.prov) && mismoLugar(fila.district, clave.dist);

export type AdondeLlega = 'llega' | 'no-llega' | 'sin-distrito';

/**
 * Con las filas de cobertura de UN courier, si llega al destino.
 *   · sin filas → «llega»: no declaró su alcance, y no se le inventa un límite (igual que el resolvedor);
 *   · alguna fila lo alcanza → «llega»;
 *   · ninguna, y el destino todavía no tiene distrito → «sin-distrito»: no está fuera de nada, le falta un dato;
 *   · ninguna, con distrito → «no-llega».
 */
export function adondeLlega(
  filas: ReadonlyArray<FilaDeCobertura>,
  destino: { department?: string | null; province?: string | null; district?: string | null },
): AdondeLlega {
  if (filas.length === 0) return 'llega';
  const clave: ClaveDelDestino = {
    dept: claveDeLugar(destino.department ?? null),
    prov: claveDeLugar(destino.province ?? null),
    dist: claveDeLugar(destino.district ?? null),
  };
  if (filas.some((f) => filaAlcanzaElDestino(f, clave))) return 'llega';
  return clave.dist ? 'no-llega' : 'sin-distrito';
}

/**
 * Las filas de cobertura del courier que se llama así (el envío guarda su transportista por nombre, como lo eligió
 * la lista). Un nombre que no está en el catálogo devuelve ninguna fila: «llega», y el registro del courier decide.
 */
export async function filasDeCoberturaDelCourier(nombre: string): Promise<FilaDeCobertura[]> {
  const { data, error } = await supabase
    .from('logistics_couriers')
    .select('logistics_courier_coverage ( department, province, district )')
    .ilike('name', nombre.trim())
    .limit(1)
    .overrideTypes<Array<{ logistics_courier_coverage: FilaDeCobertura[] | null }>, { merge: false }>();
  if (error) throw error;
  return data?.[0]?.logistics_courier_coverage ?? [];
}
